import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch } from '@/store';
import { IProduct } from '@/models/products.model';
import { getProduct } from '@/modules/Products';

/**
 * Loads product rows by product UUID (parallel requests) for block canvas preview.
 */
export function useProductsByIdsForPreview(ids: string[]): Map<string, IProduct> {
  const dispatch = useAppDispatch();
  const [byId, setById] = useState<Map<string, IProduct>>(() => new Map());

  const idKey = useMemo(
    () =>
      JSON.stringify(
        ids
          .filter(Boolean)
          .filter((id, idx, arr) => arr.indexOf(id) === idx)
          .sort()
          .slice(0, 12),
      ),
    [ids],
  );

  useEffect(() => {
    const unique = (JSON.parse(idKey) as string[]).filter(Boolean);
    if (unique.length === 0) {
      setById(new Map());
      return;
    }

    let cancelled = false;

    void Promise.all(
      unique.map((id) =>
        dispatch(getProduct(id))
          .unwrap()
          .then((res) => ({ id, product: res.data ?? null }))
          .catch(() => ({ id, product: null as IProduct | null })),
      ),
    ).then((rows) => {
      if (cancelled) return;
      const next = new Map<string, IProduct>();
      rows.forEach(({ id, product }) => {
        if (product) next.set(id, product);
      });
      setById(next);
    });

    return () => {
      cancelled = true;
    };
  }, [dispatch, idKey]);

  return byId;
}
